// ============================================
// src/App/agePullMerge.js
// MA-771 (slice age-merge-guard) — pull-side decision + write/retry logic for
// a LOCAL age the cloud never received (students.age).
//
// THE BUG: App.jsx's cloud merge was `cloudStudent.age ?? prev.studentAge`, so
// the cloud's age replaced a newer local one in two reachable flows:
//  (a) a NOT-yet-linked child whose age the parent changed in Settings (saved
//      locally, "Age updated" toast) later auto-links by name to an existing
//      cloud row still holding the old age (autoLinkByNameAge ignores age on a
//      single name match);
//  (b) onboarding / "Add a kid" reuses a same-name cloud row (App.jsx
//      handleOnboardingComplete lookup-by-name) without writing the new age.
//
// THE MECHANISM (same shape as MA-792's auto-read toggle, whose helpers are
// REUSED from ./autoReadPullMerge.js: applyToChild, isPermanentWriteError):
// a device-local flag `progress.agePendingSync`.
//   - raised by Settings for an UNLINKED child (the only Settings path that
//     writes the age locally without the cloud; a LINKED child writes nothing
//     until the cloud accepts, MA-81) and by onboarding when it reuses a
//     cloud row whose age differs (flow b: the flag, not a direct write at
//     link time, so a failure is retried and one mechanism covers both);
//   - pending  -> keep the local age, push it, clear the flag only on a
//     confirmed success for THAT value;
//   - not pending -> cloud wins (cross-device hydration), as before.
//
// HALF BANDS (4.5 / 5.5 / 6.5 / 7.5): students.age is smallint and the cloud
// REJECTS them (22P02 `invalid input syntax for type smallint: "6.5"`, the
// error MA-81's CLOUD tests inject). For a LINKED child MA-81 therefore
// refuses the change and writes nothing. For an UNLINKED child the half-band
// age is saved locally; here it can never reach the cloud, so we: never call
// the network for it (no retry-forever, no Sentry spam), keep the flag RAISED
// so the merge never silently reverts it to the cloud's whole number, and
// leave it until the parent picks a storable (whole) band, which then pushes
// normally, or the cloud already holds the same canonical age. Cost,
// accepted and documented: while such a flag is up, another device's age
// change for this child does not hydrate here (the cloud cannot hold the
// half band, so it can never agree with it).
//
// CROSS-DEVICE BASELINE (RED SHOULD-2, operator decision; replaces "last device
// to reconnect wins" for age): `progress.agePendingBaseCloud` records the cloud
// age the first merge saw while the flag was up (or the reused row's age at
// onboarding, with the row's id in `agePendingBaseStudentId`: a baseline only
// counts for the SAME row, a different row re-records it; `undefined` = not recorded yet, `null` = row held no age). On a
// later merge, if the cloud age has MOVED AWAY from that baseline, another
// device changed it: the cloud wins (hydrate, drop flag + baseline). We only
// push while the cloud still equals the baseline (or the baseline was just
// recorded). This also unfreezes a flagged half band when another device sets
// a whole age. If MA-776 widens the students.age column, `isStorableCloudAge`
// MUST change with it, or flagged half bands would never be pushed.
//
// SHARED ROWS (RED SHOULD-3): if the registry already links the cloud row to a
// DIFFERENT local child, this child's age is never pushed to it (that would
// re-band the sibling): the flag is dropped and the cloud's value wins, as
// before this slice. Why the row can be shared at all is a pre-existing reuse
// bug (handleOnboardingComplete's name lookup and autoLinkByNameAge both ignore
// rows already linked to another local child) filed separately.
//
// A PGRST116 (row not ours / gone) drops the flag silently (Sentry only): the
// parent's earlier "Age updated" toast was true when shown; the next load takes
// the cloud age. Not a regression (before this slice every load did that).
//
// Ages compare in CANONICAL form (lower-edge encoding, MA-81): a legacy
// midpoint 6.25 is "6" for the cloud. The merge never rewrites the local
// value and never re-derives start modules (MA-81 owns that, at write time).
//
// CHILD-SCOPED COMPLETION: every completion goes through applyToChild()
// (live id-scoped setter when that child is still active, otherwise a direct
// load -> transform -> save of THAT child's stored progress), so a write that
// lands after a child switch never touches another child.
// WIRING IS NOT RENDER-TESTED: App.jsx / ParentSettingsSections.tsx only call
// into this module, pinned by text pins in cloudMergeGuard.test.mjs and
// levelsDeviceHop.test.mjs.
// ============================================
import { canonicalStudentAge } from "../engine/ageBand.ts";
import { applyToChild, isPermanentWriteError } from "./autoReadPullMerge.js";

const toNum = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));
const canon = (v) => { const n = toNum(v); return Number.isFinite(n) ? canonicalStudentAge(n) : NaN; };
/** The smallint column can only hold whole ages. */
export const isStorableCloudAge = (canonicalAge) => Number.isInteger(canonicalAge);

/** Flag-clearing is only legitimate when the stored age is the one sent. */
const clearIfCurrent = (sent) => (prev) =>
  prev && prev.agePendingSync && canon(prev.studentAge) === sent
    ? { ...prev, agePendingSync: false, agePendingBaseCloud: undefined, agePendingBaseStudentId: undefined }
    : prev;

/** A stale success may have landed AFTER a newer local write: re-arm so the
 *  next load re-checks/pushes the local age (baseline re-recorded then). */
const rearm = () => (prev) =>
  prev && Number.isFinite(canon(prev.studentAge)) && !prev.agePendingSync
    ? { ...prev, agePendingSync: true, agePendingBaseCloud: undefined, agePendingBaseStudentId: undefined }
    : prev;

/** cloud age as a comparable key: a finite number, or null (no age / no row). */
const cloudKey = (v) => { const n = toNum(v); return Number.isFinite(n) ? n : null; };

/**
 * @returns {{ value: any, pending: boolean, baseline: number|null|undefined, needsPush: boolean, unstorable?: boolean }}
 */
export function resolveAgePullMerge({ cloudAge, prevAge, pending, baseline, baselineStudentId, studentId }) {
  const local = canon(prevAge);
  // A baseline belongs to the ROW it was read from (RED R2-1): after an unlink +
  // relink to a different row (MA-582 repair) it says nothing about this row, so
  // re-record it from the current row and keep the flag.
  if (baseline !== undefined && baselineStudentId !== studentId) baseline = undefined;
  const cloud = cloudKey(cloudAge);
  if (pending === true && Number.isFinite(local)) {
    // The cloud already holds this age: drop the flag, nothing to send.
    if (cloud === local) return { value: prevAge, pending: false, baseline: undefined, baselineStudentId: undefined, needsPush: false };
    // The cloud moved away from what this device saw: another device changed
    // it, the cloud wins (cross-device hydration).
    if (baseline !== undefined && cloud !== baseline) {
      return { value: cloudAge ?? prevAge, pending: false, baseline: undefined, baselineStudentId: undefined, needsPush: false, cloudMoved: true };
    }
    const base = baseline === undefined ? cloud : baseline; // record on first sight
    // Half band: the column can never hold it. Keep local, keep the flag, no push.
    if (!isStorableCloudAge(local)) return { value: prevAge, pending: true, baseline: base, baselineStudentId: studentId, needsPush: false, unstorable: true };
    return { value: prevAge, pending: true, baseline: base, baselineStudentId: studentId, needsPush: true };
  }
  // Hydration from the cloud clears a stale/meaningless flag.
  return { value: cloudAge ?? prevAge, pending: false, baseline: undefined, baselineStudentId: undefined, needsPush: false };
}

/**
 * The whole age slice of App.jsx's cloud->local merge.
 * `registry` ({ children: [{ id, supabaseStudentId }] }) is optional; when it
 * links the cloud row to ANOTHER local child, nothing is pushed (SHOULD-3).
 * @returns {{ patch: { studentAge: any, agePendingSync: boolean, agePendingBaseCloud: number|null|undefined },
 *             push: { studentId: string, localChildId: string|null, value: number } | null }}
 */
export function planAgeOnMerge({ prev, cloudStudent, localChildId, registry }) {
  const sharedWithOther = !!registry?.children?.some(
    (c) => c && c.supabaseStudentId && c.supabaseStudentId === cloudStudent?.id && c.id !== localChildId,
  );
  if (sharedWithOther && prev?.agePendingSync === true) {
    return {
      patch: { studentAge: cloudStudent?.age ?? prev?.studentAge, agePendingSync: false, agePendingBaseCloud: undefined, agePendingBaseStudentId: undefined },
      push: null,
    };
  }
  const m = resolveAgePullMerge({
    cloudAge: cloudStudent?.age,
    prevAge: prev?.studentAge,
    pending: prev?.agePendingSync,
    baseline: prev?.agePendingBaseCloud,
    baselineStudentId: prev?.agePendingBaseStudentId,
    studentId: cloudStudent?.id,
  });
  return {
    patch: { studentAge: m.value, agePendingSync: m.pending, agePendingBaseCloud: m.baseline, agePendingBaseStudentId: m.baselineStudentId },
    push: m.needsPush ? { studentId: cloudStudent.id, localChildId: localChildId ?? null, value: canon(m.value) } : null,
  };
}

/**
 * Onboarding's pending-age flags (flows b + offline create). The chosen age is
 * the NEWER value whenever the cloud row it ends up on did not receive it:
 *  - a reused same-name row holding a different age -> flag, baseline = that age;
 *  - no cloud row at all (create failed/offline) -> flag, baseline unknown
 *    (undefined: the first merge after a later auto-link records it);
 *  - a freshly created row already has this age -> nothing pending.
 * @returns {{ agePendingSync: boolean, agePendingBaseCloud: number|null|undefined, agePendingBaseStudentId: string|undefined }}
 */
export function onboardingAgePending({ reused, cloudRow, localAge }) {
  const local = canon(localAge);
  if (!Number.isFinite(local)) return { agePendingSync: false, agePendingBaseCloud: undefined, agePendingBaseStudentId: undefined };
  if (!cloudRow?.id) return { agePendingSync: true, agePendingBaseCloud: undefined, agePendingBaseStudentId: undefined };
  if (reused === true && toNum(cloudRow.age) !== local) return { agePendingSync: true, agePendingBaseCloud: cloudKey(cloudRow.age), agePendingBaseStudentId: cloudRow.id };
  return { agePendingSync: false, agePendingBaseCloud: undefined, agePendingBaseStudentId: undefined };
}

// Per-student write counters and one-shot reconnect registrations.
const writeSeq = new Map();
const reconnects = new Map();
const seqOf = (studentId) => writeSeq.get(studentId) ?? 0;
function cancelReconnect(studentId) {
  const un = reconnects.get(studentId);
  if (un) { reconnects.delete(studentId); un(); }
}

/** A newer age write (Settings on a linked child writes the cloud directly):
 *  call BEFORE it so an older in-flight pending push cannot clear/own it. */
export function noteNewerAgeWrite(studentId) {
  if (!studentId) return;
  writeSeq.set(studentId, seqOf(studentId) + 1);
  cancelReconnect(studentId);
}

async function runWrite({ studentId, localChildId, value, token, updateStudentFn, ctx, onError, onReconnect, oneShot }) {
  try {
    await updateStudentFn(studentId, { age: value });
  } catch (e) {
    if (token !== seqOf(studentId)) return { ok: false, stale: true };
    const permanent = isPermanentWriteError(e);
    if (permanent) {
      // Row is not ours / gone: it will never accept. Drop the flag (cloud
      // wins next load); the local age stays until then.
      cancelReconnect(studentId);
      applyToChild(ctx, localChildId, clearIfCurrent(value));
    } else if (onReconnect && !oneShot) {
      cancelReconnect(studentId);
      const un = onReconnect(() => {
        cancelReconnect(studentId);
        if (token !== seqOf(studentId)) return; // a newer write owns it now
        if (ctx.isChildLinked && !ctx.isChildLinked(localChildId, studentId)) return;
        void runWrite({ studentId, localChildId, value, token, updateStudentFn, ctx, onError, onReconnect, oneShot: true }).catch(() => {});
      });
      reconnects.set(studentId, un);
    }
    if (onError) onError(e, { permanent, retry: !!oneShot });
    return { ok: false, permanent };
  }
  if (token === seqOf(studentId)) {
    cancelReconnect(studentId);
    applyToChild(ctx, localChildId, clearIfCurrent(value));
    return { ok: true };
  }
  applyToChild(ctx, localChildId, rearm());
  return { ok: true, stale: true };
}

/**
 * Load-time (and reconnect) push of a pending local age. `push` is
 * planAgeOnMerge's push request (null / non-whole values never reach the net).
 */
export async function syncPendingAge({ push, ctx, updateStudentFn, onError, onReconnect }) {
  if (!push || !push.studentId || !isStorableCloudAge(push.value)) return { ok: true, skipped: true };
  return runWrite({
    studentId: push.studentId,
    localChildId: push.localChildId,
    value: push.value,
    token: seqOf(push.studentId),
    updateStudentFn,
    ctx,
    onError,
    onReconnect,
  });
}

/** TEST-ONLY: reset module counters between scenarios. */
export function __resetAgeSyncStateForTests() {
  for (const un of reconnects.values()) un();
  reconnects.clear();
  writeSeq.clear();
}
